import { FC, useState, useMemo, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Box } from "@mui/material";
import {
  types,
  useDispatchOnParams,
  Dropdown,
  utils
} from "@vilocnv/allsetra-core";
import BulkRidesUpdateDriverForm from "components/forms/objects/BulkRidesUpdateDriverForm/BulkRidesUpdateDriverForm";
import BulkRidesUpdateOrderForm from "components/forms/objects/BulkRidesUpdateOrderForm/BulkRidesUpdateOrderForm";
import DetourRouteModal, {
  DetourRouteFormValues
} from "components/modals/DetourRouteModal/DetourRouteModal";
import PrivateMileageModal, {
  PrivateMileageFormValues
} from "components/modals/PrivateMileageModal/PrivateMileageModal";

// Data
import {
  useAppDispatch,
  useAppSelector,
  useResolveAddress,
  useRideActions,
  useSilentRidesRefresh,
  useRidePermissions
} from "hooks";
import {
  selectObjectRidesState,
  selectDrawerSelectedAccountId
} from "app/data/selectors";
import {
  getObjectRidesByQueryThunk,
  setObjectSpecificRide
} from "app/features";
import { useTranslation } from "react-i18next";
import {
  DetourIcon,
  EditBoxIcon,
  PrivateMileageIcon,
  ViewOnMapIcon
} from "assets/icons";
import RidesList from "./RidesList";
import useMovementRides from "hooks/useMovementRides";
import {
  ObjectRideModeEnum,
  isOngoingRide
} from "app/data/helpers/objectRideHelpers";

interface Props {
  specificObject: types.IObject | null;
}

const ObjectRidesSection: FC<Props> = ({ specificObject }) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { showMovementRides, handleMovementRidesChange } = useMovementRides();
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    const currentItemsPerPage = searchParams.get("itemsPerPage");
    if (!currentItemsPerPage) {
      const defaultItemsPerPage =
        utils.getCommonParamsForApi().itemsPerPage || 25;
      const newParams = new URLSearchParams(searchParams);
      newParams.set("itemsPerPage", String(defaultItemsPerPage));
      if (!searchParams.get("page")) {
        newParams.set("page", "1");
      }
      setSearchParams(newParams, { replace: true });
    }
  }, []);

  // Global State
  const drawerSelectedAccountId =
    useAppSelector(selectDrawerSelectedAccountId) ?? "";

  const { objectRides, objectRidesRowCount, ridesLoading } = useAppSelector(
    selectObjectRidesState
  );

  const { resolvedAddresses, resolvedAddressLoading } =
    useResolveAddress(objectRides);

  const { silentRefreshRides } = useSilentRidesRefresh({
    accountId: drawerSelectedAccountId,
    objectId: specificObject?.uniqueId || ""
  });

  const { canUserViewRideOnMap, canUserViewAddressForRide } =
    useRidePermissions();

  // Local State
  const [loadingRideIds, setLoadingRideIds] = useState<Set<string>>(new Set());
  const [dropdownAnchorEl, setDropdownAnchorEl] = useState<null | HTMLElement>(
    null
  );
  const [selectedRowsIds, setSelectedRowsIds] = useState<Array<string>>([]);
  const [toggledClearRows, setToggleClearRows] = useState(false);

  // Local State Forms
  const [openBulkModeForm, setOpenBulkModeForm] = useState(false);
  const [openBulkDriverForm, setOpenBulkDriverForm] = useState(false);
  const [openDetourRouteModal, setOpenDetourRouteModal] = useState(false);
  const [openPrivateMileageModal, setOpenPrivateMileageModal] = useState(false);
  const [selectedRide, setSelectedRide] = useState<types.IObjectRide | null>(
    null
  );

  const { t } = useTranslation(["translation", "tableHeadingsTranslation"]);

  useEffect(() => {
    if (!drawerSelectedAccountId || !specificObject?.uniqueId) return;

    const page = parseInt(searchParams.get("page") || "1", 10);
    const itemsPerPage = parseInt(
      searchParams.get("itemsPerPage") ||
        String(utils.getCommonParamsForApi().itemsPerPage || 25),
      10
    );
    const startDate = searchParams.get("startDate");
    const endDate = searchParams.get("endDate");
    const search = searchParams.get("search");

    const params: any = {
      page,
      itemsPerPage,
      includeRidesWithoutDriver: true,
      startedWithoutIgnition: true,
      orderBy: [{ field: "startDate", ascending: true }]
    };

    if (startDate) params.startDate = startDate;
    if (endDate) params.endDate = endDate;
    if (search) params.search = search;

    dispatch(
      getObjectRidesByQueryThunk({
        params,
        accountId: drawerSelectedAccountId,
        objectId: specificObject.uniqueId
      })
    );
  }, [
    searchParams,
    drawerSelectedAccountId,
    specificObject?.uniqueId,
    dispatch
  ]);

  const formattedData = useMemo(() => {
    let reportsData = [...objectRides];
    reportsData = reportsData.filter((object: any) => {
      return showMovementRides ? true : !object.startedWithoutIgnition;
    });
    return reportsData;
  }, [objectRides, showMovementRides]);

  const handleViewOnMap = (ride: types.IObjectRide) => {
    // Permission is already checked in cellActions 'when' condition
    dispatch(setObjectSpecificRide(ride));
    navigate(
      `/dashboard/objects/${specificObject?.uniqueId}/rides/map/${ride?.uniqueId}`
    );
  };

  const handleDropdownAnchorClick = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    setDropdownAnchorEl(event.currentTarget);
  };

  const onSelectedRowsChange = ({ selectedRows }: any) => {
    const ids = selectedRows?.map((item: any) => item.uniqueId) ?? [];
    setSelectedRowsIds(ids);
  };

  const handleClearRows = () => {
    setSelectedRowsIds([]);
    setToggleClearRows(!toggledClearRows);
  };

  const fetchDataAndResetSelectableRows = useCallback(
    async (rideIdsToLoad?: string[]) => {
      // Set loading state for specific rides
      if (rideIdsToLoad && rideIdsToLoad.length > 0) {
        setLoadingRideIds(new Set(rideIdsToLoad));
      }
      await silentRefreshRides(rideIdsToLoad);
      setLoadingRideIds(new Set());
      setSelectedRowsIds([]);
      setToggleClearRows((prev) => !prev);
    },
    [silentRefreshRides]
  );

  // Clear loading state when rides data is updated (fallback)
  useEffect(() => {
    if (!ridesLoading && loadingRideIds.size > 0) {
      setLoadingRideIds(new Set());
    }
  }, [objectRides, ridesLoading]);

  const { addDetourRoute, addPrivateMileage } = useRideActions({
    accountId: drawerSelectedAccountId,
    onSuccess: (rideIds?: string[]) => fetchDataAndResetSelectableRows(rideIds)
  });

  const handleAddDetourRoute = (ride: types.IObjectRide) => {
    setSelectedRide(ride);
    setOpenDetourRouteModal(true);
  };

  const handleAddPrivateMileage = (ride: types.IObjectRide) => {
    setSelectedRide(ride);
    setOpenPrivateMileageModal(true);
  };

  const handleDetourRouteSubmit = async (values: DetourRouteFormValues) => {
    if (!selectedRide) return;
    // Set loading state for this specific ride
    setLoadingRideIds(new Set([selectedRide.uniqueId]));
    await addDetourRoute(selectedRide, values);
  };

  const handlePrivateMileageSubmit = async (
    values: PrivateMileageFormValues
  ) => {
    if (!selectedRide) return;
    // Set loading state for this specific ride
    setLoadingRideIds(new Set([selectedRide.uniqueId]));
    await addPrivateMileage(selectedRide, values);
  };

  const cellActions = useMemo(
    () => [
      {
        icon: <ViewOnMapIcon />,
        name: t("buttonsText.viewOnmap"),
        onClick: handleViewOnMap,
        when: (row: types.IObjectRide) => canUserViewRideOnMap(row)
      },
      {
        icon: <DetourIcon />,
        name: t("buttonsText.addDetourMileage"),
        onClick: handleAddDetourRoute,
        when: (row: types.IObjectRide) =>
          row.rideMode !== ObjectRideModeEnum.PRIVATE && !isOngoingRide(row)
      },
      {
        icon: <PrivateMileageIcon />,
        name: t("buttonsText.addPrivateMileage"),
        onClick: handleAddPrivateMileage,
        when: (row: types.IObjectRide) =>
          row.rideMode !== ObjectRideModeEnum.PRIVATE && !isOngoingRide(row)
      }
    ],
    [t, canUserViewRideOnMap]
  );

  return (
    <Box mt={4}>
      <RidesList
        translator={t}
        data={formattedData ?? []}
        loading={ridesLoading}
        resolvedAddresses={resolvedAddresses}
        resolvedAddressLoading={resolvedAddressLoading}
        loadingRideIds={loadingRideIds}
        cellActions={cellActions}
        canViewAddressForRide={canUserViewAddressForRide}
        primaryButton={{
          variant: "outlined",
          text: t("buttonsText.cancel"),
          size: "small",
          onClick: handleClearRows,
          disabled: !selectedRowsIds.length
        }}
        secondaryButton={{
          disabled:
            ridesLoading ||
            formattedData.length === 0 ||
            !selectedRowsIds.length,
          variant: "contained",
          text: t("buttonsText.updateItems"),
          size: "small",
          endIcon: <EditBoxIcon />,
          onClick: (event: React.MouseEvent<HTMLButtonElement>) =>
            handleDropdownAnchorClick(event)
        }}
        selectableRows={true}
        onSelectedRowsChange={onSelectedRowsChange}
        clearSelectedRows={toggledClearRows}
        showDateRangeFilter={true}
        showToggleMovementRides={true}
        showMovementRides={showMovementRides}
        onMovementRidesChange={handleMovementRidesChange}
        paginationTotalRows={objectRidesRowCount}
      />
      <Dropdown
        anchorEl={dropdownAnchorEl}
        setAnchorEl={setDropdownAnchorEl}
        items={[
          {
            name: t("common.changeRideType"),
            onClick: () => setOpenBulkModeForm(true)
          },
          {
            name: t("common.changeDriver"),
            onClick: () => setOpenBulkDriverForm(true)
          }
        ]}
      />
      {/* Forms */}
      <BulkRidesUpdateOrderForm
        open={openBulkModeForm}
        onClose={() => setOpenBulkModeForm(false)}
        rides={selectedRowsIds}
        afterSubmitActionHandler={fetchDataAndResetSelectableRows}
      />
      <BulkRidesUpdateDriverForm
        open={openBulkDriverForm}
        onClose={() => setOpenBulkDriverForm(false)}
        rides={selectedRowsIds}
        afterSubmitActionHandler={fetchDataAndResetSelectableRows}
      />

      {/* Modals */}
      <DetourRouteModal
        open={openDetourRouteModal}
        onClose={() => {
          setOpenDetourRouteModal(false);
          setSelectedRide(null);
        }}
        ride={selectedRide}
        onSubmit={handleDetourRouteSubmit}
      />
      <PrivateMileageModal
        open={openPrivateMileageModal}
        onClose={() => {
          setOpenPrivateMileageModal(false);
          setSelectedRide(null);
        }}
        ride={selectedRide}
        onSubmit={handlePrivateMileageSubmit}
      />
    </Box>
  );
};

export default ObjectRidesSection;
